/**
 * Connector status hook — currently backed by localStorage so the console
 * can demo connector lifecycle (install → enable → disable) without a
 * backend. When the EDR + Lighthouse APIs ship, swap the storage layer for
 * a React Query call against `/api/v1/connectors/{id}` — the public hook
 * surface is intentionally stable.
 */
import * as React from "react";
import type { ConnectorId, ConnectorState, ConnectorStatus } from "@/types/connectors";
import { CONNECTOR_IDS, CONNECTOR_REGISTRY } from "@/connectors/registry";
import { edrGet, EdrApiError } from "@/services/edrApi";
import { lighthouseGet, LighthouseApiError } from "@/services/lighthouseApi";

const STORAGE_KEY = "smplify_connector_state_v1";
const CHANGE_EVENT = "smplify:connector-state-changed";
const SEED_KEY = "smplify_connector_state_seeded_v1";

type StateMap = Partial<Record<ConnectorId, ConnectorState>>;

function defaultState(id: ConnectorId): ConnectorState {
  return { id, status: "not_installed" };
}

/**
 * Demo bootstrap — auto-enable the EDR and Lighthouse connectors the
 * first time the app loads in a given browser so the security pages
 * actually issue backend calls. Without this, every hook stays gated
 * behind `useConnectorEnabled(...)` and the network tab shows zero
 * requests on a fresh session. Runs exactly once per browser; users
 * can still disable from Settings → Connectors.
 */
function seedDemoConnectorsOnce() {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(SEED_KEY)) return;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const current = raw ? (JSON.parse(raw) as StateMap) : {};
    const now = new Date().toISOString();
    const next: StateMap = {
      ...current,
      edr: current.edr ?? {
        id: "edr",
        status: "enabled",
        version: "1.6.2",
        sensorCount: 0,
        lastHeartbeat: now,
        detectionMode: "monitor",
      },
      lighthouse: current.lighthouse ?? {
        id: "lighthouse",
        status: "enabled",
        version: "0.4.0",
        sensorCount: 0,
        lastHeartbeat: now,
      },
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.localStorage.setItem(SEED_KEY, "1");
  } catch {
    /* ignore quota / disabled storage */
  }
}

function readAll(): StateMap {
  if (typeof window === "undefined") return {};
  seedDemoConnectorsOnce();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StateMap;
    return parsed ?? {};
  } catch {
    return {};
  }
}


function writeAll(next: StateMap) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    /* ignore quota / disabled storage */
  }
}

function readOne(id: ConnectorId): ConnectorState {
  return readAll()[id] ?? defaultState(id);
}

function patch(id: ConnectorId, partial: Partial<ConnectorState>) {
  const all = readAll();
  const next: ConnectorState = { ...(all[id] ?? defaultState(id)), ...partial, id };
  writeAll({ ...all, [id]: next });
}

/** Install a connector — mimics the EDR spec's one-click MDM install. */
export function installConnector(id: ConnectorId) {
  // Simulate the post-install seeded state with a small fleet so the UI
  // demos. Numbers are intentionally round and harmless.
  patch(id, {
    status: "enabled",
    version: id === "edr" ? "1.6.2" : id === "lighthouse" ? "0.4.0" : "2024.7",
    sensorCount: id === "santa" ? 0 : 42,
    lastHeartbeat: new Date().toISOString(),
    detectionMode: id === "edr" ? "monitor" : undefined,
    error: undefined,
  });
}

export function disableConnector(id: ConnectorId) {
  patch(id, { status: "disabled" });
}

export function enableConnector(id: ConnectorId) {
  patch(id, { status: "enabled" });
}

export function uninstallConnector(id: ConnectorId) {
  const all = readAll();
  delete all[id];
  writeAll(all);
}

export function setDetectionMode(id: ConnectorId, mode: "monitor" | "enforce") {
  patch(id, { detectionMode: mode });
}

/** Subscribe to all connector state. */
function useConnectorState(): StateMap {
  const [state, setState] = React.useState<StateMap>(() => readAll());

  React.useEffect(() => {
    const sync = () => setState(readAll());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  return state;
}

export function useConnector(id: ConnectorId): ConnectorState {
  const all = useConnectorState();
  return all[id] ?? defaultState(id);
}

export function useAllConnectors(): ConnectorState[] {
  const all = useConnectorState();
  return CONNECTOR_IDS.map((id) => all[id] ?? defaultState(id));
}

export function isConnectorEnabled(id: ConnectorId, state?: StateMap): boolean {
  const s = state ? state[id] : readOne(id);
  return (s?.status ?? "not_installed") === "enabled";
}

/** Hook flavor of {@link isConnectorEnabled}. */
export function useConnectorEnabled(id: ConnectorId): boolean {
  return useConnector(id).status === "enabled";
}

/** Returns true once all of the connector's `requires` are enabled. */
export function useConnectorRequirementsMet(id: ConnectorId): boolean {
  const all = useConnectorState();
  const def = CONNECTOR_REGISTRY[id];
  return (def.requires ?? []).every((r) => (all[r]?.status ?? "not_installed") === "enabled");
}

// ---------- Sync ----------

export interface ConnectorSyncResult {
  id: ConnectorId;
  ok: boolean;
  message: string;
}

/**
 * Probe the connector backend for liveness. Updates `lastHeartbeat`,
 * optionally `sensorCount`, and flips status between `enabled` / `error`
 * based on reachability. Skips connectors that aren't installed.
 */
export async function syncConnector(id: ConnectorId): Promise<ConnectorSyncResult> {
  const current = readOne(id);
  if (current.status === "not_installed") {
    return { id, ok: false, message: "Not installed" };
  }

  try {
    let sensorCount: number | undefined;
    if (id === "edr") {
      const res = await edrGet<{ sensorsHealthy?: number; sensorsStale?: number }>(
        "/api/v1/connectors/edr/reports/health-insights",
      );
      const healthy = res?.sensorsHealthy ?? 0;
      const stale = res?.sensorsStale ?? 0;
      sensorCount = healthy + stale;
    } else if (id === "lighthouse") {
      const { data } = await lighthouseGet<unknown[]>("/api/v1/lighthouse/agents");
      if (Array.isArray(data)) sensorCount = data.length;
    }
    // Santa has no backend; just bump the heartbeat.

    patch(id, {
      status: "enabled",
      lastHeartbeat: new Date().toISOString(),
      ...(sensorCount != null ? { sensorCount } : {}),
      error: undefined,
    });
    return { id, ok: true, message: "Synced" };
  } catch (err) {
    const message =
      err instanceof EdrApiError || err instanceof LighthouseApiError
        ? err.message
        : err instanceof Error
          ? err.message
          : "Sync failed";
    patch(id, { status: "error", error: message, lastHeartbeat: new Date().toISOString() });
    return { id, ok: false, message };
  }
}

/** Sync every installed connector in parallel. */
export async function syncAllConnectors(): Promise<ConnectorSyncResult[]> {
  const all = readAll();
  const installed = CONNECTOR_IDS.filter(
    (id) => (all[id]?.status ?? "not_installed") !== "not_installed",
  );
  return Promise.all(installed.map((id) => syncConnector(id)));
}
